import React, { Fragment, useMemo, useState } from 'react';

import Tippy from '@tippyjs/react';

import { cn, ia } from 'lib/helpers/utility';

import './lib/Slider.scss';

/**
 * @param {import('./lib/propTypes').SliderProps} props
 */
const Slider = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  isTippy = true,
  isRange = false,
  isOptional = false,
  className,
  showMax = false
}) => {
  const [hoveredThumb, setHoveredThumb] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const values = useMemo(() => {
    if (isRange) {
      return ia(value) ? value : [min, max];
    }
    return [ia(value) ? value[0] : value];
  }, [value, isRange, min, max]);

  const handleSingleChange = (e) => {
    onChange(Number(e.target.value));
  };

  const handleRangeChange = (index, newVal) => {
    const newValues = [...values];
    newValues[index] = Number(newVal);

    if (index === 0) {
      newValues[0] = Math.min(newValues[0], newValues[1]);
    } else {
      newValues[1] = Math.max(newValues[1], newValues[0]);
    }
    onChange(newValues);
  };

  const getPercentage = (val) => ((val - min) * 100) / (max - min);

  const handleTrackClick = (e) => {
    if (!isRange) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const thumbSize = 12;
    const percent = ((e.clientX - rect.left - thumbSize / 2) / (rect.width - thumbSize)) * 100;

    let valueClicked = min + (percent / 100) * (max - min);

    valueClicked = Math.round(valueClicked / step) * step;

    valueClicked = Math.max(min, Math.min(max, valueClicked));

    const distances = values.map((v) => Math.abs(v - valueClicked));
    const closestIndex = distances.indexOf(Math.min(...distances));

    handleRangeChange(closestIndex, valueClicked);
  };

  const showHeader = label || showMax;

  return (
    <div className={cn('flex w-full flex-col ', className)}>
      {showHeader && (
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-1 text-sm">
            {label && <span className="font-medium text-neutral-800">{label}</span>}
            {isOptional && <span className="font-regular text-neutral-500">(Optional)</span>}
          </div>
          {showMax && <span className="font-regular text-sm text-neutral-800">{max}</span>}
        </div>
      )}

      <div className="relative flex h-8 w-full items-center" onClick={handleTrackClick}>
        <div className="absolute h-1.5 w-full rounded-full bg-neutral-300" />

        <div
          className="absolute h-1.5 rounded-full bg-primary-500"
          style={{
            left: isRange
              ? `calc(${getPercentage(values[0])}% + ${6 - getPercentage(values[0]) * 0.12}px)`
              : '0',
            right: `calc(${100 - getPercentage(values[isRange ? 1 : 0])}% - ${
              6 - getPercentage(values[isRange ? 1 : 0]) * 0.12
            }px)`
          }}
        />

        {values.map((val, index) => {
          const percent = getPercentage(val);
          const isHovered = hoveredThumb === index;

          return (
            <Fragment key={index}>
              <div
                className="pointer-events-none absolute flex items-center justify-center"
                style={{
                  left: `calc(${percent}% - ${percent * 0.12}px)`,
                  zIndex: 10
                }}>
                <Tippy
                  className="tippy-border"
                  visible={isTippy && (isHovered || isDragging)}
                  placement="top"
                  content={val.toString()}>
                  <div
                    className={cn(
                      'h-3 w-3 rounded-full bg-primary-500 shadow-[0px_0px_4px_0px_#0000001F] ring-2 ring-white transition-transform',
                      isHovered ? 'scale-110' : ''
                    )}
                  />
                </Tippy>
              </div>

              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={val}
                onChange={(e) =>
                  isRange ? handleRangeChange(index, e.target.value) : handleSingleChange(e)
                }
                onMouseEnter={() => setHoveredThumb(index)}
                onMouseLeave={() => setHoveredThumb(null)}
                onMouseDown={() => setIsDragging(true)}
                onMouseUp={() => setIsDragging(false)}
                className={cn(
                  'range-ghost-input absolute z-20 h-8 w-full cursor-pointer appearance-none bg-transparent opacity-0',
                  isRange && 'pointer-events-none'
                )}
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Slider;
